import { useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Lint result badge + expandable violations panel. Used in every studio under
 * AI-generated copy. Lint result shape (from backend `lint_summary`):
 *   { violations: [{start, end, match, severity, suggestion}], error_count, warning_count, clean }
 */
export function LintBadge({ lint, testid }) {
  const [expanded, setExpanded] = useState(false);

  if (!lint) return null;

  if (lint.clean) {
    return (
      <div
        data-testid={testid || 'lint-clean'}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-sans rounded-sm bg-hg-green/10 text-hg-green border border-hg-green/30"
      >
        <CheckCircle2 size={11} strokeWidth={2} />
        Brand voice: clean
      </div>
    );
  }

  const errors = lint.error_count || 0;
  const warns = lint.warning_count || 0;
  const Icon = errors > 0 ? AlertCircle : AlertTriangle;
  const color = errors > 0 ? 'red' : 'yellow';

  return (
    <div data-testid={testid || 'lint-bad'} className="space-y-1.5">
      <button
        onClick={() => setExpanded((e) => !e)}
        className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-sans rounded-sm border transition-colors ${
          color === 'red'
            ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
            : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20'
        }`}
      >
        <Icon size={11} strokeWidth={2} />
        {errors > 0 && <span>{errors} error{errors === 1 ? '' : 's'}</span>}
        {errors > 0 && warns > 0 && <span className="opacity-50">·</span>}
        {warns > 0 && <span>{warns} warning{warns === 1 ? '' : 's'}</span>}
        {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {expanded && (
        <ul
          data-testid="lint-violations-list"
          className="text-[11px] font-sans space-y-1 bg-hg-bg border border-hg-border rounded-sm p-2.5 max-h-40 overflow-y-auto"
        >
          {(lint.violations || []).map((v, i) => (
            <li
              key={i}
              data-testid={`lint-violation-${i}`}
              className="flex items-start gap-1.5"
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                  v.severity === 'error' ? 'bg-red-400' : 'bg-yellow-400'
                }`}
              />
              <div className="min-w-0">
                <span className="text-hg-text">"{v.match}"</span>
                <span className="text-hg-text-dim"> — {v.suggestion}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Inline highlighted-text renderer: render `text` with each violation span
 * wrapped in a red/yellow underline so the writer sees exactly what to fix.
 */
export function HighlightedCopy({ text, violations = [], className = '' }) {
  if (!violations || violations.length === 0) {
    return <p className={className}>{text}</p>;
  }
  // Merge overlapping spans and produce a sequence of [start,end,severity] tuples.
  const sorted = [...violations].sort((a, b) => a.start - b.start || b.end - a.end);
  const parts = [];
  let cursor = 0;
  for (const v of sorted) {
    if (v.start < cursor) continue; // skip overlapping
    if (v.start > cursor) parts.push({ text: text.slice(cursor, v.start), bad: null });
    parts.push({ text: text.slice(v.start, v.end), bad: v.severity });
    cursor = v.end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), bad: null });

  return (
    <p className={className}>
      {parts.map((p, i) =>
        p.bad ? (
          <span
            key={i}
            data-testid="highlighted-violation"
            className={
              p.bad === 'error'
                ? 'bg-red-500/15 text-red-400 underline decoration-wavy decoration-red-400/60 underline-offset-2 rounded-sm px-0.5'
                : 'bg-yellow-500/10 text-yellow-300 underline decoration-wavy decoration-yellow-300/60 underline-offset-2 rounded-sm px-0.5'
            }
            title={p.text}
          >
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        )
      )}
    </p>
  );
}

/**
 * Two-button toggle: "Plain view" vs "Show issues" so the writer can flip
 * between clean reading and the lint-highlighted version.
 */
export function HighlightToggle({ enabled, onChange, testid }) {
  return (
    <button
      data-testid={testid || 'highlight-toggle'}
      onClick={() => onChange(!enabled)}
      className="text-[10px] font-sans text-hg-text-dim hover:text-hg-text transition-colors"
    >
      {enabled ? 'Hide highlights' : 'Show issues inline'}
    </button>
  );
}
